import {
  BreakdownFormData,
  FormParamStatus,
  FormStatus,
  ResampledData,
  SingleValueParamData,
} from "@/lib/form/types"
import { EnumsOf } from "@/lib/utils"
import { Schema } from "@/rmpm/lib/schema"

export interface BaseFormSchema {
  status?: FormParamStatus
}

/**
 * Root schema
 */
export interface FormSchema {
  status?: FormStatus
  products: ProductSchema[]
}

export interface HandoverFormSchema extends FormSchema {
  handoverFromEmployeeCode: string
  handoverFromEmployeeSessionId: string
  products: HandoverProductSchema[]
}

export interface HandoverProductSchema extends ProductSchema {
  plantName: string
  shiftName: string
  employeeName: string
  createdAt: string
}

export type DraftFormSchema = Omit<FormSchema, "status">

export interface ProductSchema extends BaseFormSchema {
  id: string
  name: string
  productionLines: ProductionLineSchema[]
}

export interface ProductionLineSchema extends BaseFormSchema {
  id: string
  serverSheetId?: string
  name: string
  filledAt?: string
  shiftStartTime?: string
  shiftEndTime?: string
  shiftName?: string
  machines: MachineSchema[]
}

export interface MachineSchema extends BaseFormSchema {
  id: string
  name: string
  breakdown?: BreakdownFormData[]
  internalProcesses: InternalProcessSchema[]
}

export interface InternalProcessSchema extends BaseFormSchema {
  id: string
  name: InternalProcessName
  parameters: ParamSchema[]
}

export type InternalProcessName = "DEFAULT" | (string & {})

export const ParamValidationType = {
  LIMIT: "LIMIT",
  CHOICE: "CHOICE",
  FLAG: "FLAG",
  NONE: "NONE",
} as const
export type ParamValidationType = EnumsOf<typeof ParamValidationType>

export interface ParamSchema
  extends BaseFormSchema,
  Partial<SingleValueParamData> {
  id?: string
  parameterId?: string
  productId?: string
  name: string
  subtext?: string
  mode: ParamSchemaMode
  type: ParamSchemaType
  validationType: ParamValidationType
  unit: string
  limit?: ParamSchemaLimit
  window?: ParamWindow[]
  choices?: ParamChoice[]
  options?: DropdownParamOption[]
  formula?: FormulaSchema
  value?: string | number
}

export interface ParamSchemaLimit {
  min: number
  max: number
  gradeRanges: ParamGradeRange[]
}

export interface FormulaSchema extends BaseFormSchema {
  id: string
  formulaText: string
  inputs: FormulaInputSchema[]
  factors?: Record<string, number>
}

export interface FormulaInputSchema {
  id: string
  variable: string
  name: string
  type: ParamSchemaType
  unit: string
}

export interface ParamChoice {
  id: string
  value: string
  label: string
  isCorrect: boolean
}

export interface ParamWindow {
  id: string
  /**
   * ISO UTC timestamp
   */
  startTime: string
  /**
   * ISO UTC timestamp
   */
  endTime: string
  status?: FormParamStatus

  value?: string | number
  remark?: string
  gradeLabel?: ParamGradeLabel
  recordedAt?: string
  sequenceOrder?: number
  overtime?: boolean
  early?: boolean
  resampleCleared?: boolean
  resample?: ResampledData<ParamWindow>
}

export type AnySchema =
  | FormSchema
  | ProductionLineSchema
  | MachineSchema
  | InternalProcessSchema
  | ParamSchema
  | ProductSchema
  | FormulaSchema
  | RadioParamSchema
  | Schema

export interface ParamGradeRange {
  id: string
  min: number
  max: number
  gradeLabel: ParamGradeLabel
}

export const ParamGradeLabel = {
  Green: "Green",
  Yellow: "Yellow",
  Orange: "Orange",
  None: "None",
} as const
export type ParamGradeLabel =
  (typeof ParamGradeLabel)[keyof typeof ParamGradeLabel]

export const ParamSchemaMode = {
  TEST: "TEST",
  INFO: "INFO",
} as const
export type ParamSchemaMode = EnumsOf<typeof ParamSchemaMode>

export const ParamSchemaType = {
  VALUE_NUMBER: "VALUE_NUMBER",
  VALUE_TEXT: "VALUE_TEXT",
  CHOICE: "CHOICE",
  RADIO: "RADIO",
  DATE: "DATE",
  DROPDOWN: "DROPDOWN",
  CARD: "CARD",
  CARD_SECTION: "CARD_SECTION",
  SECTION: "SECTION",
  PLAIN: "PLAIN",
  NONE: "NONE",
} as const
export type ParamSchemaType = EnumsOf<typeof ParamSchemaType>

export interface RadioParamSchema {
  id: string
  name: string
  mode: ParamSchemaMode
  type: ParamSchemaType
  mandatory?: boolean
  options: RadioParamOption[]
}

export interface RadioParamOption {
  id: string
  label: string
  value: string
}

export interface DropdownParamSchema {
  id: string
  name: string
  mode: ParamSchemaMode
  type: ParamSchemaType
  mandatory?: boolean
  options: DropdownParamOption[]
}

export interface DropdownParamOption {
  id: string
  label: string
  value: string
}
